// src/features/medicine-inventory/components/patient/PatientSearch.jsx

import React, { useState, useEffect, useRef } from "react";
import { FaSearch, FaUser, FaIdCard, FaPhone } from "react-icons/fa";
import api from "../../services/api";

const PatientSearch = ({ onSelectPatient, selectedPatient }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Search API call
  const handleSearch = async () => {
    if (searchTerm.trim().length < 2) {
      setPatients([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.searchPatients(searchTerm);

      setPatients(response.data || []);
      setShowDropdown(true);
    } catch (error) {
      console.error("Failed to search patients:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Select patient
  const handleSelectPatient = (patient) => {
    onSelectPatient(patient);
    setSearchTerm(patient.name);
    setShowDropdown(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Label */}
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Patient <span className="text-red-500">*</span>
      </label>

      {/* Search Box */}
      <div className="relative">
        <input
          type="text"
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Search by name, ID, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
            }
          }}
        />

        {/* Search Icon */}
        <button
          onClick={handleSearch}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600"
        >
          <FaSearch />
        </button>

        {/* Loading Spinner */}
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {/* Dropdown Results */}
      {showDropdown && patients.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">

          {patients.map((patient) => (
            <div
              key={patient.id}
              className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
              onClick={() => handleSelectPatient(patient)}
            >
              <div className="flex items-center space-x-3">

                <div className="bg-blue-100 p-2 rounded-full">
                  <FaUser className="text-blue-600" />
                </div>

                <div className="flex-1">

                  <div className="font-medium text-gray-900">
                    {patient.full_name} ({patient.employee_code})
                  </div>

                  <div className="flex items-center space-x-4 text-sm text-gray-500">

                    <span className="flex items-center">
                      <FaIdCard className="mr-1 h-3 w-3" />
                      {patient.patient_code}
                    </span>

                    <span className="flex items-center">
                      <FaPhone className="mr-1 h-3 w-3" />
                      {patient.mobile}
                    </span>

                  </div>

                  <div className="text-xs text-gray-400">
                    {patient.age} years • {patient.gender}
                  </div>

                </div>

              </div>
            </div>
          ))}

        </div>
      )}

      {/* Selected Patient */}
      {selectedPatient && (
        <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">

          <div className="flex items-center justify-between">

            <div>

              <p className="font-medium text-green-800">
                {selectedPatient.full_name} (Employee ID: {selectedPatient.employee_code})
              </p>

              <p className="text-sm text-green-600">
                Patient ID: {selectedPatient.patient_code} • {selectedPatient.age} years •{" "}
                {selectedPatient.gender}
              </p>

            </div>

            <button
              onClick={() => {
                onSelectPatient(null);
                setSearchTerm('');
              }}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Change
            </button>

          </div>

        </div>
      )}
    </div>
  );
};

export default PatientSearch;